// UserAuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { decryptLegacyCJ } from "../components/cryptoLegacyCJ";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
const userAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const USERS_COLLECTION = "user"; // Firestore collection name

  // ===== Basic Firebase Auth helpers (optional) =====
  function logIn(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }
  function signUp(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }
  function logOut() {
    return signOut(auth);
  }
  function googleSignIn() {
    const googleAuthProvider = new GoogleAuthProvider();
    return signInWithPopup(auth, googleAuthProvider);
  }

  // If you keep OTP for anything else
  let recaptchaVerifier;
  function setUpRecaptha(number) {
    if (!recaptchaVerifier) {
      recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {},
      });
      recaptchaVerifier.render();
    }
    return signInWithPhoneNumber(auth, number, recaptchaVerifier);
  }

  // ===== Realtime DB lookup by phone =====
  // Firestore "user" collection; field may be "phoneNumber" (camelCase) or "phone_number" (snake_case).
  // Tries multiple value formats to match how the doc was written.
  // Nigerian numbers: react-phone-number-input gives E.164 without leading 0 (+234901841932); DB may store +2340901841932.
  async function getUserByPhone(phoneNumber) {
    if (!phoneNumber || typeof phoneNumber !== "string") return null;
    const normalized = phoneNumber.trim();
    const variants = [
      normalized,
      normalized.startsWith("+") ? normalized.slice(1) : `+${normalized}`,
      ` ${normalized}`,
      `${normalized} `,
    ];
    const nigeriaMatch = normalized.match(/^\+234(\d{9})$/);
    if (nigeriaMatch) variants.push(`+2340${nigeriaMatch[1]}`);
    const toTry = [...new Set(variants)];
    const fieldNames = ["phoneNumber", "phone_number"];

    for (const fieldName of fieldNames) {
      for (const candidate of toTry) {
        const q = query(
          collection(db, USERS_COLLECTION),
          where(fieldName, "==", candidate)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const docSnap = snap.docs[0];
          return { id: docSnap.id, ...docSnap.data() };
        }
      }
    }
    console.error("[UserAuthContext] getUserByPhone: no match. Tried fields", fieldNames.join(", "), "with values:", toTry.map((v) => JSON.stringify(v)).join(" | "));
    return null;
  }

  // Verify password (with migration support)
  async function verifyPasswordForPhone(phoneNumber, password) {
    console.error("[UserAuthContext] verifyPasswordForPhone – phone:", JSON.stringify(phoneNumber), "hasPassword:", !!password);
    try {
      const userDoc = await getUserByPhone(phoneNumber);
      if (!userDoc) {
        console.error("[UserAuthContext] User not found for phone:", JSON.stringify(phoneNumber));
        return { ok: false, message: "User not found" };
      }
  
      const storedPassword = (userDoc.password || "").trim();
      if (!storedPassword) return { ok: false, message: "No password set" };
  
      // Decrypt and verify password
      const decrypted = await decryptLegacyCJ(storedPassword);
      if (decrypted !== password) {
        return { ok: false, message: "Invalid password" };
      }
  
      // Fetch user profile with invited farms from API (use stored phone so it matches DB format, e.g. +2340901841932)
      let userProfile = null;
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5000';
        const encodedPhoneNumber = encodeURIComponent(userDoc.phoneNumber || phoneNumber);
        const response = await fetch(`${API_BASE_URL}/user/${encodedPhoneNumber}/verify`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          userProfile = await response.json();
          console.log('User profile fetched:', userProfile);
        } else {
          console.warn('Failed to fetch user profile, using basic data');
        }
      } catch (apiError) {
        console.warn('Error fetching user profile from API:', apiError);
      }
  
      // Create a minimal Firebase-compatible user object
      const firebaseUser = {
        uid: userDoc.userId || userDoc.id,
        email: userDoc.email,
        phoneNumber: userDoc.phoneNumber,
        displayName: userDoc.userName,
        emailVerified: userDoc.verified,
        // Add token to the user object
        token: userDoc.token,
        // Add provider data
        providerData: [{
          providerId: 'phone',
          uid: userDoc.phoneNumber,
          displayName: userDoc.userName,
          email: userDoc.email,
          phoneNumber: userDoc.phoneNumber
        }],
        // Add user profile data if available
        profile: userProfile
      };
  
      // IMPORTANT: Set the user in your auth context
      setUser(firebaseUser);
  
      // Return success with the user data
      return { 
        ok: true, 
        user: firebaseUser 
      };
  
    } catch (error) {
      console.error('Error in verifyPasswordForPhone:', error);
      return { 
        ok: false, 
        message: error.message || "Authentication failed" 
      };
    }
  }

  // Single auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          setUser({
            ...firebaseUser,
            token,  // Store the token for API calls
          });
        } catch (error) {
          console.error("Error getting ID token:", error);
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <userAuthContext.Provider
      value={{
        user,
        logIn,
        signUp,
        logOut,
        googleSignIn,
        setUpRecaptha,
        getUserByPhone,
        verifyPasswordForPhone,
      }}
    >
      {children}
    </userAuthContext.Provider>
  );
}

export function useUserAuth() {
  return useContext(userAuthContext);
}
